import {
  Flex,
  Image,
  Tag,
  Box,
  Text,
  useColorModeValue,
  keyframes,
} from "@chakra-ui/react";
import styled from "@emotion/styled";
import React, { useState, useEffect } from "react";

import ccstype2 from "../../assets/img/chargePointsTab/ccstype2.png";
import AC1 from "../../assets/img/chargePointsTab/actype1.png";
import AC2 from "../../assets/img/chargePointsTab/ac2.png";
import CDMO from "../../assets/img/chargePointsTab/chademo.png";
import DOMESTIC_D from "../../assets/img/chargePointsTab/domestic_d.png";

import { useRecoilValue } from "recoil";
import { activeConnectorState } from "atoms/activeConnectorState";
import Pulsing from "components/feedback/Pulsing";
import ConnectorStatusCard from "./ConnectorStatusCard";

// Glass morphism styled components
const GlassMorphismCard = styled(Box)`
  backdrop-filter: blur(16px);
  background: rgba(255, 255, 255, 0.7);
  border-radius: 20px;
  padding: 1rem;
  transition: all 0.3s ease;
  transform-style: preserve-3d;
  perspective: 1000px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(35, 169, 121, 0.25);
  position: relative;
  overflow: hidden;

  @media (min-width: 768px) {
    padding: 1.5rem;
  }

  &:before {
    content: "";
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(
      circle,
      rgba(35, 169, 121, 0.08) 0%,
      rgba(255, 255, 255, 0) 70%
    );
    z-index: 0;
  }

  &:after {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.2) 0%,
      rgba(255, 255, 255, 0) 50%
    );
    z-index: 0;
  }

  &:hover {
    transform: translateY(-5px) rotateX(2deg);
    box-shadow: 0 8px 24px rgba(35, 169, 121, 0.2);
    border-color: rgba(35, 169, 121, 0.5);

    &:before {
      animation: pulse-radial 2s infinite;
    }
  }

  @keyframes pulse-radial {
    0% {
      opacity: 0.5;
    }
    50% {
      opacity: 0.8;
    }
    100% {
      opacity: 0.5;
    }
  }
`;

const StatusTag = styled(Tag)`
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  font-weight: 600;
  letter-spacing: 0.5px;

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      45deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transform: translateX(-100%);
  }

  &:hover:before {
    transform: translateX(100%);
    transition: transform 0.75s ease;
  }
`;

const pulse = keyframes`
  0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(35, 169, 121, 0.7); }
  70% { transform: scale(1); box-shadow: 0 0 0 10px rgba(35, 169, 121, 0); }
  100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(35, 169, 121, 0); }
`;

const ConnectorType = styled(Tag)`
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(35, 169, 121, 0.3);
  backdrop-filter: blur(4px);
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  margin-bottom: 1rem;
  position: relative;
  overflow: hidden;
  z-index: 1;

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      rgba(35, 169, 121, 0.1) 0%,
      rgba(255, 255, 255, 0) 50%,
      rgba(35, 169, 121, 0.1) 100%
    );
    z-index: -1;
    transform: translateX(-100%);
    transition: transform 0.6s ease;
  }

  &:hover:before {
    transform: translateX(100%);
  }
`;

const ConnectorCard = () => {
  const activeConnector = useRecoilValue(activeConnectorState);
  const [status, setStatus] = useState(activeConnector?.Status);
  const [typeOfConnector, setTypeOfConnector] = useState(
    activeConnector?.Connectors[0]?.Standard,
  );

  // Update status and connector type when activeConnector changes
  useEffect(() => {
    if (activeConnector) {
      setStatus(activeConnector.Status);
      setTypeOfConnector(activeConnector.Connectors[0]?.Standard);
    }
  }, [activeConnector]);

  // Theme colors
  const bgColor = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.700", "white");

  let logo, userReadableType;
  switch (typeOfConnector) {
    case "AC Type 1":
      logo = AC1;
      userReadableType = "AC Type 1";
      break;
    case "AC Type 2":
      logo = AC2;
      userReadableType = "AC Type 2";
      break;
    case "CCS Type 2":
      logo = ccstype2;
      userReadableType = "CCS Type 2";
      break;
    case "CHAdeMO":
      logo = CDMO;
      userReadableType = "CHAdeMO";
      break;

    case "DOMESTIC_D":
      logo = DOMESTIC_D;
      userReadableType = "3 Pin Socket (16A - 230V)";
      break;

    default:
      logo = ccstype2;
      userReadableType = typeOfConnector;
      break;
  }

  // Status configuration
  const getStatusConfig = () => {
    const currentStatus = activeConnector?.Status || status;
    switch (currentStatus) {
      case "Charging":
        return {
          color: "white",
          bg: "linear-gradient(135deg, #23A979 0%, #ff9966 100%)",
          animation: `${pulse} 2s infinite`,
        };
      case "Preparing":
        return {
          color: "white",
          bg: "linear-gradient(135deg, #f5a623 0%, #f7b955 100%)",
        };
      case "Available":
        return {
          color: "white",
          bg: "linear-gradient(135deg, #48BB78 0%, #68D391 100%)",
        };
      default:
        return {
          color: "white",
          bg: "linear-gradient(135deg, #718096 0%, #A0AEC0 100%)",
        };
    }
  };

  const statusConfig = getStatusConfig();

  return (
    <GlassMorphismCard
      width={{
        base: "100%",
        sm: "100%",
        md: "300px",
      }}
      height={{
        base: "auto",
        md: "100%",
      }}
      minHeight="300px"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      position="relative"
      mr={{
        base: 0,
        md: 6,
      }}
      mb={{
        base: 4,
        md: 0,
      }}
      _before={{
        content: '""',
        position: "absolute",
        top: 0,
        right: 0,
        width: "150px",
        height: "150px",
        borderRadius: "full",
        background:
          "radial-gradient(circle, rgba(35, 169, 121, 0.15) 0%, rgba(255, 255, 255, 0) 70%)",
        zIndex: 0,
      }}
      _after={{
        content: '""',
        position: "absolute",
        bottom: "10px",
        left: "10px",
        width: "100px",
        height: "100px",
        borderRadius: "full",
        background:
          "radial-gradient(circle, rgba(35, 169, 121, 0.1) 0%, rgba(255, 255, 255, 0) 70%)",
        zIndex: 0,
      }}
    >
      {/* Status dot indicator */}
      <Box
        position="absolute"
        top={4}
        right={4}
        width="12px"
        height="12px"
        borderRadius="full"
        bg={
          (activeConnector?.Status || status) === "Charging"
            ? "brand.500"
            : (activeConnector?.Status || status) === "Preparing"
              ? "#f5a623"
              : (activeConnector?.Status || status) === "Available"
                ? "#48BB78"
                : "#718096"
        }
        boxShadow={`0 0 0 rgba(35, 169, 121, 0.4)`}
        animation={
          (activeConnector?.Status || status) === "Charging"
            ? `${pulse} 2s infinite`
            : "none"
        }
        transition="background-color 0.3s ease"
        key={`status-dot-${activeConnector?.Status || status}`} // Force re-render when status changes
      />
      {/* Connector image with stylish background */}
      <Box
        position="relative"
        mb={4}
        p={4}
        borderRadius="md"
        boxShadow="0 4px 12px rgba(0, 0, 0, 0.05)"
        border="1px solid rgba(35, 169, 121, 0.3)"
        bgGradient="linear-gradient(135deg, rgba(35, 169, 121, 0.08) 0%, rgba(255, 153, 102, 0.05) 100%)"
        _before={{
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          borderRadius: "md",
          background:
            "radial-gradient(circle at top right, rgba(35, 169, 121, 0.15), transparent 70%)",
          zIndex: 0,
        }}
        zIndex={1}
      >
        <Image
          maxW="120px"
          minW="80px"
          src={logo}
          alt={`${typeOfConnector} connector`}
          transition="transform 0.3s ease"
          _hover={{ transform: "scale(1.05)" }}
        />
      </Box>
      {/* Connector type with glass morphism effect */}
      <ConnectorType>{userReadableType}</ConnectorType>
      {/* Status indicator with animation */}
      <StatusTag
        size="lg"
        borderRadius="md"
        py={2}
        px={4}
        bg={statusConfig.bg}
        color={statusConfig.color}
        animation={statusConfig.animation}
        boxShadow="0 4px 12px rgba(0, 0, 0, 0.1)"
        mt={2}
        maxW="90%"
        textAlign="center"
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
        key={activeConnector?.Status || status} // Force re-render when status changes
      >
        {activeConnector?.Status || status}
      </StatusTag>
    </GlassMorphismCard>
  );
};

export default ConnectorCard;
